import { Box, CircularProgress, Paper, Typography, Button, IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { DOCUMENT_TYPE } from "../../constants/documentType";
import { documentServices } from "../../services/documentServices";
import { useSnackbar } from "../../utils/useSnackbar";
import PageHeader from "../ui/PageHeader";
import ActionProgressDialog from '../common/ActionProgressDialog';
import "../../css/Document.css";

const DocumentPreview = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id, documentType, fieldData, employeeId } = location.state || {};
    const [previewHtml, setPreviewHtml] = useState("");
    const [loading, setLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const { showSnackbar, SnackbarComponent } = useSnackbar();

    const [scale, setScale] = useState(1);
    const containerRef = useRef(null);

    useEffect(() => {
        const updateScale = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth;
                if (width > 0 && width < 820) {
                    setScale(width / 820);
                } else {
                    setScale(1);
                }
            }
        };

        let observer;
        if (containerRef.current && window.ResizeObserver) {
            observer = new ResizeObserver(updateScale);
            observer.observe(containerRef.current);
        } else {
            window.addEventListener('resize', updateScale);
        }

        updateScale();
        const timeoutId = setTimeout(updateScale, 100);

        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener('resize', updateScale);
            clearTimeout(timeoutId);
        };
    }, [previewHtml, loading]);

    useEffect(() => {
        if (!documentType) {
            navigate("/home/documents");
            return;
        }

        const loadPreview = async () => {
            try {
                const html = await documentServices.getPreviewDocument({
                    userId: employeeId,
                    documentType,
                    fieldData
                });
                setPreviewHtml(html);
            } catch (err) {
                showSnackbar("Failed to load preview", "error");
            } finally {
                setLoading(false);
            }
        };

        loadPreview();
    }, [documentType, fieldData, navigate, showSnackbar]);

    const handleDownload = async () => {
        setIsGenerating(true);
        try {
            await documentServices.generateDocument({
                id,
                userId: employeeId,
                documentType,
                fieldData
            });
            showSnackbar("Document Generated Successfully!", "success");
            navigate('/home/documents');
        } catch (err) {
            showSnackbar("Failed to generate document. Document already exists.", "error");
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Box className="document-page-container">
            <PageHeader 
                title={`${DOCUMENT_TYPE[documentType] ?? documentType} Preview`} 
                left={
                    <IconButton onClick={() => navigate(-1)} size="medium">
                        <ArrowBackIcon />
                    </IconButton>
                }
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', width: { xs: "100%", sm: "auto" }, gap: 2, bgcolor: 'white', p: 1, borderRadius: 3, boxShadow: '0 2px 0.625rem rgba(0,0,0,0.05)' }}>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() =>
                            navigate("/home/documents", {
                                state: { id, employeeId, documentType, fieldData }
                            })
                        }
                        sx={{ borderRadius: 2, px: { xs: 2, sm: 3 }, flex: { xs: 1, sm: "none" } }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                        onClick={handleDownload}
                        disabled={isGenerating}
                        sx={{ borderRadius: 2, px: { xs: 2, sm: 4 }, flex: { xs: 1, sm: "none" } }}
                    >
                        {isGenerating ? "Generating..." : "Download"}
                    </Button>
                </Box>
            </Box>

            {/* Document Container */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center"
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        width: "100%",
                        maxWidth: 900,
                        height: { xs: "80vh", sm: "85vh" },
                        p: { xs: 0, sm: 2 },
                        display: "flex",
                        flexDirection: "column"
                    }}
                >
                    {loading && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexGrow: 1
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && previewHtml && (
                        <Box ref={containerRef} sx={{ flexGrow: 1, overflow: "hidden", position: "relative", width: "100%", height: "100%", bgcolor: '#f8f9fa' }}>
                            <Box
                                component="iframe"
                                title="document-preview"
                                srcDoc={previewHtml}
                                sx={{
                                    position: scale < 1 ? "absolute" : "static",
                                    top: 0,
                                    left: 0,
                                    width: scale < 1 ? "820px" : "100%",
                                    height: scale < 1 ? `${100 / scale}%` : "100%",
                                    border: "none",
                                    transform: scale < 1 ? `scale(${scale})` : "none",
                                    transformOrigin: "top left",
                                    backgroundColor: "white",
                                }}
                            />
                        </Box>
                    )}
                </Paper>
            </Box>
            {SnackbarComponent}
            <ActionProgressDialog open={isGenerating} message="Generating Document..." />
        </Box>
    );
};

export default DocumentPreview;